import AccessTime from "@mui/icons-material/AccessTime";
import Done from "@mui/icons-material/Done";
import LanguageIcon from "@mui/icons-material/Language";
import PlayArrow from "@mui/icons-material/PlayArrow";
import { Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { useCreateWebAutomationJobs } from "hooks/useCreateWebAutomationJobs";
import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useAlerts } from "../../../../../components/Alerts/AlertProvider";
import {
  CreateWebAutomationJobInput,
  ImplementationFacilitiesQuery,
  JobStatus,
  JobTask,
  WebAutomationAction,
} from "../../../../../generated-graphql/graphql";
import { useBulkActionContext } from "../Facilities/FacilitiesBulkActionMenu";

type Row = ImplementationFacilitiesQuery["facilities"]["items"][0];

export function QueueExtractionsModal() {
  const {
    selectedRows,
    dialogState,
    onClose: handleCloseQueueExtractionsModal,
  } = useBulkActionContext();

  const { tenantId } = useParams();
  const alerts = useAlerts();

  const showQueueExtractionsModal = dialogState?.action === "queueExtractions";
  const selectedReportingYear =
    dialogState?.action === "queueExtractions" ? dialogState.year : new Date().getFullYear();

  const queueableExtractionCount = useMemo(() => {
    return selectedRows.filter((item) => canQueueExtraction(selectedReportingYear, item)).length;
  }, [selectedRows, selectedReportingYear]);

  const [createWebAutomationJobs, { error: mutationError }] = useCreateWebAutomationJobs({
    refetchQueries: ["ImplementationFacilities", "ExtractionsWebAutomationJobs"],
    variables: {
      input: selectedRows
        .filter((item) => canQueueExtraction(selectedReportingYear, item))
        .map((selectedRow): CreateWebAutomationJobInput => {
          const stateOrTerritory = selectedRow?.state;

          return {
            tenantId: tenantId ?? "",
            facilityId: selectedRow.id,
            status: JobStatus.Queued,
            action: WebAutomationAction.ExtractData,
            reportingYear: Number.isNaN(selectedReportingYear)
              ? undefined
              : Number(selectedReportingYear),
            evidenceOfCompletionActions: [],
            stepError: "",
            stateOrTerritory: stateOrTerritory ?? "",
            forceClassicSubmissionStrategy: false,
            task: JobTask.WebAutomation,
            parameters: {}, // todo: Jason, what is this supposed to be?
          };
        }),
    },
  });

  const handleConfirmCreateWebAutomationJobs = useCallback(async () => {
    await createWebAutomationJobs();

    if (mutationError) {
      alerts.error("There was an error initiating extractions");
    } else {
      alerts.success(`Successfully queued extractions`);
    }

    handleCloseQueueExtractionsModal();
  }, [createWebAutomationJobs, alerts, mutationError, handleCloseQueueExtractionsModal]);

  const columns: GridColDef<(typeof selectedRows)[number]>[] = [
    {
      headerName: "Organization",
      field: "tenant",
      valueGetter: (_, row) => row.tenant?.name,
    },
    { headerName: "Facility", field: "name", flex: 1 },
    { headerName: "Customer", field: "customerFacilityId" },
    { headerName: "State", field: "state" },
    {
      headerName: "Reporting Year",
      field: "reportingYear",
      flex: 1,
      renderCell: () => selectedReportingYear,
    },
    {
      headerName: "Extraction Status",
      field: "extractionStatus",
      flex: 1,
      renderCell: ({ row }) => (
        <ExtractionStatus selectedReportingYear={selectedReportingYear} facilityRow={row} />
      ),
    },
    {
      headerName: "Will Queue",
      field: "willQueue",
      flex: 1,
      renderCell: ({ row }) => (
        <ExtractionWillStart selectedReportingYear={selectedReportingYear} facilityRow={row} />
      ),
    },
  ];
  return (
    <Dialog
      open={showQueueExtractionsModal}
      fullWidth
      maxWidth="md"
      onClose={handleCloseQueueExtractionsModal}
      title="Confirm Queue Extractions"
      primaryActionButtonProps={{
        onClick: handleConfirmCreateWebAutomationJobs,
        startIcon: <PlayArrow />,
        disabled: queueableExtractionCount === 0,

        saveText: "Queue",
      }}
    >
      <DataGrid rows={selectedRows} columns={columns} />
    </Dialog>
  );
}

function ExtractionStatus(props: { selectedReportingYear: number; facilityRow?: Row }) {
  const match = props.facilityRow?.jobs?.find(
    (item) => item.reportingYear === props.selectedReportingYear
  );

  if (match) {
    if (match.status === JobStatus.Succeeded) {
      return (
        <Stack direction={"row"}>
          <Typography
            sx={{
              marginRight: "0.5rem",
            }}
          >
            SUCCEEDED
          </Typography>
          <Done color="success" />
        </Stack>
      );
    }

    if (match.status === JobStatus.Running) {
      return (
        <Stack direction={"row"}>
          <Typography
            sx={{
              marginRight: "0.5rem",
            }}
          >
            RUNNING
          </Typography>
          <LanguageIcon />
        </Stack>
      );
    }

    if (match.status === JobStatus.Queued) {
      return (
        <Stack direction={"row"}>
          <Typography
            sx={{
              marginRight: "0.5rem",
            }}
          >
            QUEUED
          </Typography>
          <AccessTime />
        </Stack>
      );
    }

    return (
      <Stack direction={"row"}>
        <Typography
          sx={{
            marginRight: "0.5rem",
          }}
        >
          {match.status}
        </Typography>
      </Stack>
    );
  }

  return <></>;
}

function ExtractionWillStart(props: { selectedReportingYear: number; facilityRow?: Row }) {
  if (canQueueExtraction(props.selectedReportingYear, props.facilityRow)) {
    return (
      <Typography
        sx={{
          marginRight: "0.5rem",
        }}
      >
        YES
      </Typography>
    );
  }

  return (
    <Typography
      sx={{
        marginRight: "0.5rem",
      }}
    >
      NO
    </Typography>
  );
}

function canQueueExtraction(selectedReportingYear: number, facilityRow?: Row): boolean {
  const match = facilityRow?.jobs?.find((item) => item.reportingYear === selectedReportingYear);
  if (!match) {
    return true;
  }

  if (
    match.status === JobStatus.Succeeded ||
    match.status === JobStatus.Cancelled ||
    match.status?.includes("FAILED")
  ) {
    return true;
  }

  return false;
}
